import type { MessageReadReceipts } from '@repo/types/message';
import type { UserPublic } from '@repo/types/users';

import { useOptimisticConversationReadState } from '@repo/api/chat/useOptimisticConversationReadState';
import { usePostReadEntityNotifications } from '@repo/api/notification/usePostReadEntityNotification';
import { NotificationType } from '@repo/types/enums/notificationType';
import { MessageCircle } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MessageCard from '@/components/features/chat/message/MessageCard';
import MessageCardSkeleton from '@/components/features/chat/message/MessageCardSkeleton';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { useMessageReadObserver } from '@/hooks/chat';
import { chatSocketService } from '@/services/chat-service';
import { useCredentials } from '@/store/auth-store';
import { useChatStore } from '@/store/chat-store';

export default function ChatContent() {
  const credentials = useCredentials();
  const connectionStatus = useChatStore((state) => state.connectionStatus);
  const messages = useChatStore((state) => state.messages);
  const hasMore = useChatStore((state) => state.hasMore);
  const isLoading = useChatStore((state) => state.loading);
  const chatId = useChatStore((state) => state.chatId);

  const { mutate: readEntityNotifications } = usePostReadEntityNotifications();

  const [unreadAnchorMessageId, setUnreadAnchorMessageId] = useState<number | null>(null);
  const hasSetInitialAnchorRef = useRef<boolean>(false);

  const isEmpty = useMemo<boolean>(() => messages.length === 0 && !isLoading && !hasMore && connectionStatus === 'connected', [messages, isLoading, hasMore, connectionStatus]);

  const unreadMessageIds = useMemo<number[]>(() => messages.filter((message) => (message.id < 0 ? false : true)).map((message) => message.id), [messages, credentials.id]);

  const firstUnreadMessageId = useMemo<number | null>(() => {
    if (!unreadMessageIds.length) return null;
    return unreadMessageIds[0] ?? null;
  }, [unreadMessageIds]);

  useEffect(() => {
    setUnreadAnchorMessageId(null);
    hasSetInitialAnchorRef.current = false;
  }, [chatId]);

  useEffect(() => {
    if (hasSetInitialAnchorRef.current) {
      return;
    }
    if (connectionStatus !== 'connected' || isLoading || messages.length === 0) {
      return;
    }
    hasSetInitialAnchorRef.current = true;
    setUnreadAnchorMessageId(firstUnreadMessageId);
  }, [connectionStatus, isLoading, messages.length, firstUnreadMessageId]);

  const stackedReadReceiptsByMessageId = useMemo<Map<number, MessageReadReceipts[]>>(() => {
    const grouped = new Map<number, MessageReadReceipts[]>();
    const seenUsers = new Set<number>();
    const users = chatSocketService.getUsers();
    const totalParticipants = users.filter((u) => u.id !== credentials.id).length;

    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message?.id === undefined || !message.readReceipts?.length) continue;
      for (const receipt of message.readReceipts) {
        // Our own receipt is recorded on every message we scroll past; we are
        // not a "reader" of a message we are already looking at.
        if (receipt.id === credentials.id || seenUsers.has(receipt.id)) {
          continue;
        }
        seenUsers.add(receipt.id);

        let list = grouped.get(message.id);
        if (!list) {
          list = [];
          grouped.set(message.id, list);
        }
        list.push(receipt);
      }

      // Only an early exit; the participant list is a socket snapshot that can
      // be empty or incomplete, so never let it cut the walk short.
      if (totalParticipants > 0 && seenUsers.size >= totalParticipants) {
        break;
      }
    }

    return grouped;
  }, [messages, credentials.id]);

  const update = useOptimisticConversationReadState(chatId, credentials);
  const addReadReceipt = useChatStore((state) => state.addReadReceipt);
  const markMessageAsRead = useCallback(
    (id: number) => {
      chatSocketService.markMessageAsRead(id);

      // Add current user's read receipt optimistically
      const currentUser: UserPublic = {
        id: credentials.id,
        lvl: credentials.lvl,
        name: credentials.name,
        surname: credentials.surname,
        email: credentials.email,
        avatarURL: credentials.avatarURL?.replace(import.meta.env.VITE_API_DOMAIN, '') ?? null,
      };
      addReadReceipt(id, currentUser);

      update(id);
      if (chatId) {
        readEntityNotifications({
          entityId: String(chatId),
          type: NotificationType.Chat,
        });
      }
    },
    [update, addReadReceipt, credentials]
  );

  const { containerRef } = useMessageReadObserver({
    messageIds: unreadMessageIds,
    onVisible: markMessageAsRead,
    enabled: connectionStatus === 'connected',
    resetKey: chatId,
  });

  const nextPage = () => {
    chatSocketService.requestHistory();
  };

  return (
    <div
      ref={containerRef}
      aria-label={'Chat messages'}
      aria-live={'polite'}
      className={'flex min-h-0 w-full flex-1 flex-col-reverse overflow-x-hidden overflow-y-scroll overscroll-contain scroll-smooth p-2 md:p-4'}
      style={{
        overflowAnchor: 'none',
      }}
    >
      <div className={'flex w-full flex-col'}>
        {(connectionStatus === 'connecting' || isLoading) && <MessageCardSkeleton />}
        <InfiniteScroll hasNextPage={hasMore} isLoading={isLoading} isFetching={isLoading} next={nextPage} threshold={0.65} reverse={true} rootMargin={'0px 0px 100px 0px'}>
          {hasMore && !isLoading && connectionStatus === 'connected' && <MessageCardSkeleton />}
        </InfiniteScroll>

        {messages.map((message, index) => {
          const shouldRenderUnreadDivider: boolean = unreadAnchorMessageId != null && message.id === unreadAnchorMessageId;

          const messageKey = message.id ? message.id : message.tempUUID;

          return (
            <Fragment key={messageKey}>
              {shouldRenderUnreadDivider && (
                <div className={'my-3 flex w-full items-center gap-3'} aria-label={'Unread messages divider'}>
                  <div className={'bg-border-primary h-px flex-1'} />
                  <span className={'text-text-tertiary text-xs font-medium'}>Nieprzeczytane</span>
                  <div className={'bg-border-primary h-px flex-1'} />
                </div>
              )}

              <MessageCard
                data-message-id={message.id}
                message={message}
                stackedReadReceipts={stackedReadReceiptsByMessageId.get(message.id)}
                previousMessage={index > 0 ? messages[index - 1] : undefined}
              />
            </Fragment>
          );
        })}
        {isEmpty && (
          <EmptyState className="mb-26">
            <EmptyStateCircles className={'z-0'} />
            <FeaturedIcon size={'lg'} className={'z-10'}>
              <MessageCircle />
            </FeaturedIcon>
            <EmptyStateContent>
              <EmptyStateTitle>Brak wiadomości</EmptyStateTitle>
              <EmptyStateDescription>
                Brak wiadomości dla wskazanego chatu, <br />
                bądź pierwszym który wyśle wiadomość.
              </EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        )}
      </div>
    </div>
  );
}
